<template>
  <ModuleContainer>
    <v-card
      style="position: absolute; top: 10px; left: 50px;"
      class="complaints-control-container"
    >
      <!-- Search -->
      <v-text-field
        ref="searchField"
        placeholder="Search Complaints"
        append-icon="search"
        solo
        hide-details
        clearable
        v-model="filters.query"
        :loading="loading"
        @input="getComplaints"
      />

      <!-- Result -->
      <SimpleList
        :items="resultComplaints"
        clickable
        :loading="loading"
        style="max-height: 18.75em; overflow: auto"
        @click="onClickResultItem"
      />
    </v-card>

    <slot />
  </ModuleContainer>
</template>

<script>
import L from "@/plugins/leaflet";
import ModuleMixin from "@/components/Map/ModuleMixin";
import SimpleList from "@/components/SimpleList";
import api from "@/api/index";
import { CanvasBuilder } from "@/utils/Canvas";
import LayerPopupVue from "./LayerPopup";
import Vue from "vue";

import { debounce } from "@/utils/Debounce";

export default {
  mixins: [ModuleMixin],

  components: {
    SimpleList
  },

  data() {
    return {
      filters: {
        query: ""
      },

      getComplaintsCancelToken: undefined,

      complaints: undefined,

      loading: false,

      layerGroup: undefined,
      complaintMapping: {}
    };
  },

  computed: {
    geojsonSource() {
      const geojson = {
        type: "FeatureCollection",
        features: []
      };

      if (!this.complaints) return geojson;

      for (let i = 0; i < this.complaints.length; i++) {
        const complaint = this.complaints[i];

        geojson.features.push({
          type: "Feature",
          id: complaint.id,
          properties: {
            description: {
              title: `Complaint #${complaint.id}`,
              table: {
                Status: `<span class="px-2 py-1" style="background-color: ${complaint.status.color}; color: white">${complaint.status.name}</span>`,
                "Created By": complaint.created_by && complaint.created_by.name,
                "Assigned To":
                  complaint.assigned_to && complaint.assigned_to.name,
                Category: complaint.category && complaint.category.name,
                Nature:
                  (complaint.nature && complaint.nature.name) ||
                  complaint.nature_string,
                Remarks: complaint.remarks
              },
              images: complaint.images && complaint.images.map(img => img.url)
            },
            paint: {
              radius: 6,
              fillColor: complaint.status.color,
              fillOpacity: 1,
              color: "white",
              weight: 1
            }
          },
          geometry: {
            type: "Point",
            coordinates: [complaint.longitude, complaint.latitude]
          }
        });
      }

      return geojson;
    },

    resultComplaints() {
      if (!this.complaints) return undefined;

      const complaints = [];

      if (this.complaints.length > 0) {
        complaints.push({
          header: `Complaints (${this.complaints.length})`
        });
      }

      for (let i = 0; i < this.complaints.length; i++) {
        const complaint = this.complaints[i];

        complaints.push({
          id: complaint.id,
          avatar: this.createIcon(complaint.status.color),
          avatarTitle: complaint.status.name,
          title: `${complaint.id} - ${complaint.category.name}`,
          subtitle: `${complaint.created_by && complaint.created_by.name} - ${
            complaint.created_at
          } <br /> ${
            complaint.nature ? complaint.nature.name : complaint.nature_string
          }`,
          latitude: complaint.latitude,
          longitude: complaint.longitude
        });

        complaints.push({
          divider: true,
          inset: true
        });
      }

      return complaints;
    }
  },

  watch: {
    geojsonSource() {
      this.redraw();
    }
  },

  methods: {
    onInit() {
      if (!this.map) return;

      if (!this.map._loaded) {
        this.map.once("load", () => {
          this.layerGroup = L.markerClusterGroup().addTo(this.map);
          this.redraw();
        });
      } else {
        this.layerGroup = L.markerClusterGroup().addTo(this.map);
        this.redraw();
      }
    },

    getComplaints: debounce(async function() {
      try {
        this.loading = true;
        this.complaints = undefined;

        if (!this.filters.query) return;

        const res = await api.getComplaints({
          q: this.filters.query || undefined
        });

        this.complaints = res.data.data;
      } finally {
        this.loading = false;
      }
    }, 500),

    redraw() {
      this.clearGroup();

      for (let i = 0; i < this.geojsonSource.features.length; i++) {
        const feature = this.geojsonSource.features[i];

        const layer = this.createLayer(feature);
        this.layerGroup.addLayer(layer);

        this.complaintMapping[feature.id] = layer;
      }

      const bounds = this.layerGroup.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds);
      }
    },

    clearGroup() {
      this.complaintMapping = {};
      this.layerGroup.clearLayers();
    },

    createLayer(feature) {
      let layer = null;

      switch (feature.geometry.type) {
        case "Point":
          layer = this.createPoint(feature);
          break;
      }

      if (layer) {
        this.bindPopUp(layer, feature);
      }

      return layer;
    },

    createPoint(feature) {
      return L.circleMarker(
        [feature.geometry.coordinates[1], feature.geometry.coordinates[0]],
        feature.properties.paint
      );
    },

    bindPopUp(layer, feature) {
      if (!feature.properties.description) return;

      const content = new Vue(LayerPopupVue);

      content.title = feature.properties.description.title;
      content.table = feature.properties.description.table;
      content.images = feature.properties.description.images;

      content.$mount();

      layer.bindPopup(content.$el);
    },

    createIcon(color) {
      return CanvasBuilder.create(40, 40)
        .drawCircle(20, 20, 6, {
          fillStyle: color,
          strokeStyle: "rgba(0, 0, 0, 0.12)"
        })
        .toBase64();
    },

    onClickResultItem(item) {
      const marker = this.complaintMapping[item.id];

      if (!marker) return;

      this.map.flyTo(marker.getLatLng(), 18);

      this.map.once("moveend", function() {
        marker.openPopup();
      });
    }
  }
};
</script>

<style scoped>
.complaints-control-container {
  width: 25em;
}

@media (max-width: 600px) {
  .complaints-control-container {
    width: 20em;
  }
}
</style>
